import moment from "moment/moment";

// Payment status options
export const PAYMENT_STATUS_OPTIONS = [
	{ value: 'pending', label: 'Pending' },
	{ value: 'in_progress', label: 'In Progress' },
	{ value: 'completed', label: 'Completed' },
	{ value: 'cancelled', label: 'Cancelled' },
	{ value: 'refunded', label: 'Refunded' },
	{ value: 'partially_paid', label: 'Partially Paid' },
	{ value: 'on_hold', label: 'On Hold' },
	{ value: 'credit', label: 'Credit' },
];

// Helper function to get status color
export const getStatusColor = (status) => {
	const statusColors = {
		'pending': { border: '#FFA500', background: '#FFF8E1' },
		'in_progress': { border: '#FFA500', background: '#FFF8E1' },
		'completed': { border: '#52c41a', background: '#F6FFED' },
		'cancelled': { border: '#dc3545', background: '#FFF1F0' },
		'refunded': { border: '#722ed1', background: '#F9F0FF' },
		'partially_paid': { border: '#1890ff', background: '#E6F7FF' },
		'on_hold': { border: '#faad14', background: '#FFFBE6' },
		'credit': { border: '#722ed1', background: '#F9F0FF' },
	};
	return statusColors[status] || { border: '#d9d9d9', background: '#FAFAFA' };
};

// Helper function to format status text
export const formatStatus = (status) => {
	if (!status) return "Pending";
	return status
		.split('_')
		.map(word => word.charAt(0).toUpperCase() + word.slice(1))
		.join(' ');
};

// Helper function to get the latest update date and time
export const getLatestUpdate = (card) => {
	const updateFields = [
		{ date: card.designer_update_date, time: card.designer_update_time },
		{ date: card.cutting_update_date, time: card.cutting_update_time },
		{ date: card.secendory_update_date, time: card.secendory_update_time },
		{ date: card.accountent_update_date, time: card.accountent_update_time },
		{ date: card.production_update_date, time: card.production_update_time },
		{ date: card.emboridary_update_date, time: card.emboridary_update_time },
		{ date: card.admin_update_date, time: card.admin_update_time },
		{ date: card.packing_update_date, time: card.packing_update_time },
		{ date: card.dispatch_update_date, time: card.dispatch_update_time },
		{ date: card.payment_update_date, time: card.payment_update_time },
	];

	const validUpdates = updateFields
		.filter((update) => update.date && update.time)
		.map((update) => {
			const dateTimeString = `${update.date} ${update.time}`;
			return moment(dateTimeString, "DD/MM/YYYY HH:mm:ss").valueOf();
		});

	if (validUpdates.length === 0) {
		return "N/A";
	}

	const latestTimestamp = Math.max(...validUpdates);
	return moment(latestTimestamp).format("DD/MM/YYYY HH:mm");
};

// Date/Time display formatters
export const formatDateTimeDisplay = (value, options = {}) => {
	if (!value) return "—";
	const parsed = new Date(value);
	if (!Number.isNaN(parsed.getTime())) {
		return parsed.toLocaleString("en-IN", {
			year: "numeric",
			month: "short",
			day: "numeric",
			hour: "2-digit",
			minute: "2-digit",
			...options,
		});
	}
	return value;
};

export const formatDateDisplay = (value) =>
	formatDateTimeDisplay(value, { hour: undefined, minute: undefined });

export const formatTimeDisplay = (value) => {
	if (!value) return "—";
	if (value.includes(":")) {
		const [hours, minutes] = value.split(":");
		return `${hours?.padStart(2, "0")}:${minutes?.padStart(2, "0")}`;
	}
	return value;
};

// WhatsApp helper functions
export const formatPhoneForWhatsApp = (phone) => {
	if (!phone) return null;
	let digits = String(phone).replace(/\D/g, "");
	if (!digits) return null;
	if (digits.startsWith("00")) {
		digits = digits.slice(2);
	}
	if (digits.startsWith("0") && digits.length > 1) {
		digits = digits.slice(1);
	}
	if (digits.length === 10) {
		digits = `91${digits}`;
	}
	return digits;
};

export const composeDispatchMessage = ({
	clientName,
	companyName,
	jobcards = [],
	deliveryDate,
	deliveryTime,
	deliveryNote,
}) => {
	const jobcardLines = jobcards.length
		? jobcards
				.map((jobcard) => {
					const quantityText = jobcard.quantity ? `${jobcard.quantity} pcs` : null;
					const shippingText = jobcard.shipping_address ? `\n   Ship: ${jobcard.shipping_address}` : "";
					return `• Jobcard #${jobcard.serial_number || jobcard.id}${quantityText ? ` (${quantityText})` : ""}${shippingText}`;
				})
				.join("\n")
		: "• Jobcard info unavailable";

	return (
		`Hello ${clientName || "there"},\n\n` +
		`Dispatch update for ${companyName || "your order"}:\n` +
		`${jobcardLines}\n\n` +
		`Delivery Date: ${deliveryDate || "N/A"}\n` +
		`Delivery Time: ${deliveryTime || "N/A"}\n` +
		`Note: ${deliveryNote || "—"}\n\n` +
		`Please let us know if you need anything else.\n` +
		`- Dispatch Team`
	);
};

// Date validation helpers
export const checkExpired = (record) => {
	const today = moment().startOf("day");
	const deliverDate = moment(record.deliver_date, "DD/MM/YYYY").startOf("day");
	return deliverDate.isBefore(today);
};

export const checkDeliveryToday = (record) => {
	const today = moment().startOf("day");
	const deliverDate = moment(record.deliver_date, "DD/MM/YYYY").startOf("day");
	return deliverDate.isSame(today, "day");
};

// Priority hover text color mapping
export const PRIORITY_HOVER_TEXT_COLOR = {
	0: "#FFFFFF",
	1: "#000000",
	2: "#000000",
	3: "#FFFFFF",
	4: "#FFFFFF"
};

// Stage to field mapping for work done validation
// Uses string keys matching STAGES values from Constants.js
export const STAGE_TO_FIELD_MAP = {
	'creation': 'is_creation_work_done',
	'cutting': 'is_cutting_work_done',
	'secendory_process': 'is_secondary_process_work_done',
	'embroidary': 'is_embroidery_work_done',
	'production': 'is_production_work_done',
	'packing': 'is_packing_work_done',
	'account': 'is_billing_work_done',
	'payment': 'is_payment_work_done',
	'dispatch': 'is_dispatch_work_done',
};
